import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, BarChart3, Users, TrendingUp, TrendingDown,
  Award, BookOpen, AlertCircle, ShieldCheck,
} from "lucide-react";
import { useAdminGetCourseDetailsQuery, useAdminGetCourseDietDetailsQuery } from "../../api/adminExamCoursesApi";
import { useAdminGetDietsQuery } from "../../api/examsApi";

const GRADE_COLORS: Record<string, string> = {
  A: "bg-emerald-50 text-emerald-700 border-emerald-100",
  B: "bg-blue-50 text-blue-700 border-blue-100",
  C: "bg-amber-50 text-amber-700 border-amber-100",
  F: "bg-rose-50 text-rose-700 border-rose-100",
};

function DietAnalysisPanel({ courseId, dietId }: { courseId: string; dietId: string }) {
  const { data, isLoading, isError } = useAdminGetCourseDietDetailsQuery(
    { courseId, dietId }, { skip: !courseId || !dietId }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="py-16 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-gray-100 mb-3" />
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No data for this diet yet</p>
      </div>
    );
  }

  const analysis = data?.analysis ?? {
    total: 0, average: null, highest: null, lowest: null,
    passCount: 0, passRate: null, distribution: { A: 0, B: 0, C: 0, F: 0 },
  };
  const students: any[] = data?.students ?? [];

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Students",      val: analysis.total,                                          icon: Users,       color: "indigo"  },
          { label: "Average Score", val: analysis.average != null ? `${analysis.average}%` : "—", icon: BarChart3,   color: "purple"  },
          { label: "Highest",       val: analysis.highest != null ? `${analysis.highest}%` : "—", icon: TrendingUp,  color: "emerald" },
          { label: "Lowest",        val: analysis.lowest  != null ? `${analysis.lowest}%`  : "—", icon: TrendingDown, color: "rose"   },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Pass rate + grade distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-amber-500" />
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Pass Rate</div>
          </div>
          <div className="flex items-end gap-2 mb-3">
            <span className="text-4xl font-black text-gray-900">
              {analysis.passRate != null ? `${analysis.passRate}%` : "—"}
            </span>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
              {analysis.passCount} / {analysis.total} passed
            </span>
          </div>
          {analysis.total > 0 && (
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${analysis.passRate ?? 0}%` }} />
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Grade Distribution</div>
          <div className="space-y-3">
            {Object.entries(analysis.distribution ?? {}).map(([grade, count]) => {
              const pct = analysis.total > 0 ? Math.round(((count as number) / analysis.total) * 100) : 0;
              return (
                <div key={grade} className="flex items-center gap-3">
                  <span className={`w-7 h-6 flex items-center justify-center rounded-lg text-[9px] font-black border ${GRADE_COLORS[grade] ?? "bg-gray-50 text-gray-400 border-gray-100"}`}>{grade}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-indigo-400" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] font-black text-gray-400 w-16 text-right">{count as number} ({pct}%)</span>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mt-3">A ≥ 70 · B 60–69 · C 50–59 · F &lt; 50</p>
        </div>
      </div>

      {/* Students table */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Students &amp; Scores ({students.length})
          </div>
        </div>

        {students.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No results recorded yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left bg-white border-b border-gray-50">
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">#</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Name</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Reg. No.</th>
                <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Score</th>
                <th className="px-8 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Grade</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Examiner</th>
                <th className="px-8 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {students.map((s: any, i: number) => {
                const score = s.score;
                const grade = score >= 70 ? "A" : score >= 60 ? "B" : score >= 50 ? "C" : "F";
                return (
                  <tr key={s.id ?? i} className="hover:bg-gray-50/80 transition-all">
                    <td className="px-8 py-5 text-[10px] font-black text-gray-300">{i + 1}</td>
                    <td className="px-8 py-5">
                      <div className="font-black text-gray-900 uppercase tracking-tight">{s.student_name ?? "—"}</div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{s.student_reg ?? "—"}</div>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <span className="font-black text-gray-900">{score != null ? `${score}%` : "—"}</span>
                    </td>
                    <td className="px-8 py-5 text-center">
                      <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm ${GRADE_COLORS[grade] ?? "bg-gray-50 text-gray-400 border-gray-100"}`}>
                        {grade}
                      </span>
                    </td>
                    <td className="px-8 py-5">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{s.examiner ?? "—"}</div>
                    </td>
                    <td className="px-8 py-5 text-center">
                      <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm bg-gray-50 text-gray-400 border-gray-100">
                        {s.status ?? "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function AdminExamCourseDetailsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: course, isLoading: courseLoading } = useAdminGetCourseDetailsQuery(id, { skip: !id });
  const { data: diets = [], isLoading: dietsLoading } = useAdminGetDietsQuery();

  const [selectedDiet, setSelectedDiet] = useState("");

  const selCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  if (courseLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="max-w-xl mx-auto py-24 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-gray-100 mb-3" />
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300 mb-6">Course not found</p>
        <Link to="/exams/admin/courses"
          className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all">
          <ArrowLeft className="w-4 h-4" /> Back to Courses
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-start gap-5">
          <button onClick={() => navigate("/exams/admin/courses")}
            className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm mt-1 shrink-0">
            <ArrowLeft className="w-4 h-4 text-gray-500" />
          </button>
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200 shrink-0">
            <BookOpen className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[10px] font-black text-indigo-600 tracking-widest bg-indigo-50 px-3 py-1.5 rounded-xl border border-indigo-100">
                {course.code}
              </span>
              <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border shadow-sm ${course.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-gray-50 text-gray-400 border-gray-100"}`}>
                {course.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase mt-2">{course.name}</h1>
            {course.description && (
              <p className="text-gray-400 text-sm mt-1.5 font-medium">{course.description}</p>
            )}
          </div>
        </div>

        {(course.diets ?? []).length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-50">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Used in Diets</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {(course.diets as any[]).map((d: any) => (
                <button key={d.diet_id}
                  onClick={() => setSelectedDiet(String(d.diet_id))}
                  className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border transition-all active:scale-95 ${
                    selectedDiet === String(d.diet_id)
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-500 border-gray-200 hover:border-indigo-300 hover:text-indigo-600"}`}>
                  {d.diet_name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Per-Diet Analysis */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-4 h-4 text-purple-500" />
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Per-Diet Analysis</div>
        </div>

        <div className="space-y-1 mb-8">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Exam Diet</label>
          {dietsLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading diets…
            </div>
          ) : (
            <select value={selectedDiet} onChange={(e) => setSelectedDiet(e.target.value)} className={selCls}>
              <option value="">— Select a diet —</option>
              {diets.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>
              ))}
            </select>
          )}
        </div>

        {selectedDiet ? (
          <DietAnalysisPanel courseId={id} dietId={selectedDiet} />
        ) : (
          <div className="py-16 text-center">
            <BarChart3 className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
              Select a diet to view analysis
            </p>
          </div>
        )}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-8 h-8 text-indigo-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">Data Integrity</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Course results shown here are drawn from published exam batches only. Scores under review or in draft are not reflected in this analysis.
          </p>
        </div>
      </div>
    </div>
  );
}
